import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface InviteClientModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (organizationEmail: string) => Promise<any>;
  saving: boolean;
  error: string | null;
}

export function InviteClientModal({ open, onOpenChange, onSubmit, saving, error }: InviteClientModalProps) {
  const [organizationEmail, setOrganizationEmail] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!organizationEmail.trim()) {
      return;
    }

    await onSubmit(organizationEmail.trim());
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setOrganizationEmail("");
    }
    onOpenChange(open);
  };

  const isFormValid = organizationEmail.trim() && organizationEmail.includes("@");

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Invite Client as Vendor</DialogTitle>
          <DialogDescription>
            Enter the organization email. If the organization exists, they will receive an email to accept or decline the vendor relationship. If not, they will receive an invitation to create an account.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="organization-email">Organization Email *</Label>
              <Input
                id="organization-email"
                type="email"
                value={organizationEmail}
                onChange={(e) => setOrganizationEmail(e.target.value)}
                placeholder="organization@example.com"
                required
              />
              <p className="text-sm text-muted-foreground">
                {organizationEmail && organizationEmail.includes("@")
                  ? "An email will be sent to this address."
                  : "Enter a valid email address for the organization."}
              </p>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !isFormValid}>
              {saving ? "Sending..." : "Send Invite"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
